/**
 * 55 — the router and the render loop.
 *
 * State is a plain object and a render() function. That's not a joke and it's
 * not a placeholder — it's four screens, and a framework here would cost more
 * kilobytes than the app itself. Every one of those kilobytes is a second
 * Trevor spends looking at a blank phone in a pub with two bars of signal.
 *
 * Routing is by hash. That means: no SPA-fallback rule to configure on Pages,
 * no 404s on a hard refresh, and — the reason that actually matters — the app
 * still routes when it's opened from the service worker cache with no network
 * at all.
 */

// styles.css is linked from index.html, not imported here — that keeps it
// render-blocking in <head>, so the boot skeleton paints already styled
// instead of flashing unstyled while the bundle parses.
import { mountShelf, type Screen } from './shelf';
import { mountSong } from './song';
import { mountUpload } from './upload';
import { mountActivity } from './activity';
import { mountOffline } from './offline';
// The Settings screen is built by a sibling task. We only wire the route + a
// nav item to it; if that file lands after this one, the router still compiles
// against its signature.
import { mountSettings } from './settings';
import { mountLogin, currentMember } from './login';
import { mountNowBar } from './nowbar';
import { checkVetting } from './vetting';
import { heartbeat, type Member } from './api';
import { h, clear, icon, initTheme, toast } from './ui';

/* ------------------------------------------------------------------ routes */

type Route =
  | { name: 'shelf' }
  | { name: 'song'; id: string }
  | { name: 'add' }
  | { name: 'activity' }
  | { name: 'downloads' }
  | { name: 'settings' };

function parse(hash: string): Route {
  const p = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  if (p[0] === 'song' && p[1]) return { name: 'song', id: decodeURIComponent(p[1]) };
  if (p[0] === 'add') return { name: 'add' };
  if (p[0] === 'activity') return { name: 'activity' };
  if (p[0] === 'downloads') return { name: 'downloads' };
  if (p[0] === 'settings') return { name: 'settings' };
  return { name: 'shelf' };
}

/** The app's whole state. */
const app: { route: Route; screen: Screen | null } = {
  route: parse(location.hash),
  screen: null,
};

export function go(hash: string): void {
  if (location.hash === hash) render();
  else location.hash = hash;
}

/* ------------------------------------------------------------------ render */

const screenEl = document.getElementById('screen') as HTMLElement;

function render(): void {
  app.screen?.destroy();
  app.screen = null;
  clear(screenEl);
  screenEl.scrollTop = 0;
  window.scrollTo(0, 0);

  switch (app.route.name) {
    case 'shelf':
      app.screen = mountShelf(screenEl, (id) => go(`#/song/${encodeURIComponent(id)}`));
      break;
    case 'song':
      app.screen = mountSong(screenEl, app.route.id, () => go('#/'));
      break;
    case 'add':
      app.screen = mountUpload(screenEl, () => go('#/'));
      break;
    case 'activity':
      // mountActivity/mountOffline return a bare teardown, not a Screen — wrap
      // it in the { destroy } shape the router stores and tears down like the
      // rest.
      app.screen = { destroy: mountActivity(screenEl) };
      break;
    case 'downloads':
      app.screen = { destroy: mountOffline(screenEl) };
      break;
    case 'settings':
      // Same shape as activity/offline: a bare teardown, wrapped so the router
      // tears it down like everything else.
      app.screen = { destroy: mountSettings(screenEl) };
      break;
  }

  paintTabs();
}

window.addEventListener('hashchange', () => {
  app.route = parse(location.hash);
  render();
});

/* ------------------------------------------------------------- the tab bar */

const tabbar = document.getElementById('tabbar') as HTMLElement;

/* Icons the bottom nav needs that ui.ts doesn't carry. Drawn to match
   `.tab svg` — stroked, currentColor, 24-box — like every other icon here. */
const svgActivity = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12h4l3-8 4 16 3-8h4"/></svg>`;
const svgOffline = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v11M8 11l4 4 4-4M5 19h14"/></svg>`;
const svgSettings = `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3.1"/><path d="M12 2.4v3M12 18.6v3M2.4 12h3M18.6 12h3M5.1 5.1l2.1 2.1M16.8 16.8l2.1 2.1M18.9 5.1l-2.1 2.1M7.2 16.8l-2.1 2.1"/></svg>`;

/**
 * The bottom nav, left → right:
 *   SONGS · OFFLINE · (⊕ FAB) · SETTINGS · [ ACTIVITY (Jonathan) | your name ]
 *
 * The centre ⊕ is a raised red circle — the one and only way to reach the
 * record screen, and the loudest thing on the bar. Everything else is
 * monochrome and icon-forward; the active tab's icon + label turn red (the
 * top-edge hairline is gone — that red now belongs to the player's scrub line).
 *
 * The far-right slot is identity. Jonathan (admin) gets ACTIVITY there — his
 * window into the room. Everyone else gets their own NAME, which balances the
 * bar and quietly says "you're signed in as Rob". Tapping the name opens
 * Settings (where Sign out lives).
 */
function buildTabs(me: Member | null): void {
  clear(tabbar);
  const isAdmin = me?.role === 'admin';

  // Home IS the record — the disc icon, labelled SONGS (not "2026", not "Shelf").
  tabbar.appendChild(navItem('shelf', '#/', 'Songs', icon.shelf));
  tabbar.appendChild(navItem('downloads', '#/downloads', 'Offline', svgOffline));
  tabbar.appendChild(fabItem());
  tabbar.appendChild(navItem('settings', '#/settings', 'Settings', svgSettings));
  // Far right: Activity for Jonathan, your name for everyone else.
  if (isAdmin) tabbar.appendChild(navItem('activity', '#/activity', 'Activity', svgActivity));
  else tabbar.appendChild(nameItem(me));

  tabbar.hidden = false;
  paintTabs();
}

/** The identity slot for a non-admin: a bone initial disc + first name, opening Settings. */
function nameItem(me: Member | null): HTMLAnchorElement {
  const first = (me?.name ?? 'You').trim();
  const initial = first.charAt(0).toUpperCase() || '·';
  return h('a', { class: 'tab tab-name', href: '#/settings', 'data-tab': 'settings', 'aria-label': `Signed in as ${first} — open settings` }, [
    h('span', { class: 'tab-ic tab-initial', text: initial }),
    h('span', { class: 'tab-label', text: first }),
  ]);
}

function navItem(name: Route['name'], hash: string, label: string, svg: string): HTMLAnchorElement {
  return h('a', { class: 'tab', href: hash, 'data-tab': name }, [
    h('span', { class: 'tab-ic', html: svg }),
    h('span', { class: 'tab-label', text: label }),
  ]);
}

/** The centre + . A red circular FAB, larger than its neighbours and raised. */
function fabItem(): HTMLAnchorElement {
  return h('a', { class: 'fab', href: '#/add', 'data-tab': 'add', 'aria-label': 'Record or add a recording' }, [
    h('span', { class: 'fab-ic', 'aria-hidden': 'true', html: icon.plus }),
  ]);
}

function paintTabs(): void {
  // Both the plain tabs and the FAB carry data-tab, so light whichever matches.
  for (const a of tabbar.querySelectorAll<HTMLElement>('[data-tab]')) {
    const active =
      a.dataset.tab === app.route.name ||
      // the song screen is a child of the shelf, so the shelf stays lit
      (a.dataset.tab === 'shelf' && app.route.name === 'song');
    if (active) a.setAttribute('aria-current', 'page');
    else a.removeAttribute('aria-current');
  }
}

/* ------------------------------------------------------- the now-playing bar */

/**
 * The footer transport. Lives outside #screen and never tears down, so changing
 * route can't interrupt playback or rebuild it. All of its behaviour — the
 * full-width waveform, the note dots, the lyrics strip, the transport, the
 * expand/collapse — lives in nowbar.ts.
 */
const npEl = document.getElementById('nowplaying') as HTMLElement;
mountNowBar(npEl, { openSong: (id) => go(`#/song/${encodeURIComponent(id)}`) });

/* ---------------------------------------------------------------- offline */

function paintNet(): void {
  document.body.classList.toggle('is-offline', !navigator.onLine);
}
window.addEventListener('online', () => {
  paintNet();
  toast('Back online.');
});
window.addEventListener('offline', () => {
  paintNet();
  toast("You're offline. You can still see the shelf.", true);
});

/* ------------------------------------------------------------------ boot */

/**
 * The login gate.
 *
 * The session cookie lasts a year, so for everyone except a brand-new phone
 * this is a single GET that comes back with a member and is never seen again.
 * That is the whole point: the band signs in once, ever.
 *
 * If we cannot reach the API at all (they're on a mountain, or Cloudflare is
 * having a day), we do NOT dump them at a login screen they cannot use — a PIN
 * pad with no network is a locked door with no key. We let them through to the
 * shelf, where the service worker still has the shell and anything they've
 * downloaded. The API will 401 the moment it comes back, and they'll see the
 * pills then.
 */
async function boot(): Promise<void> {
  initTheme();
  paintNet();

  let me: Awaited<ReturnType<typeof currentMember>> = null;
  let reachable = true;
  try {
    me = await currentMember();
  } catch {
    reachable = false; // network died, not "logged out"
  }

  if (!me && reachable) {
    tabbar.hidden = true;
    npEl.hidden = true;
    clear(screenEl);
    mountLogin(screenEl, (freshMe) => {
      // A fresh session. Rebuild the world as that member — including whether
      // the ACTIVITY tab exists for them. The footer paints itself off player
      // state, so there's nothing to kick here.
      buildTabs(freshMe);
      render();
      void checkVetting(freshMe, go);
    });
    return;
  }

  buildTabs(me);
  render();
  startHeartbeat();
  // Brad/Johhny get the "lyrics need vetting" notice if any takes are waiting.
  void checkVetting(me, go);
}

void boot();

/**
 * Presence heartbeat — what makes "here now" on the Activity screen honest.
 *
 * We ping only while the tab is actually visible and foreground. The moment
 * the phone locks, the app is backgrounded, or the tab is closed, the pings
 * stop and the server sees you go stale; within ~75s you correctly show as
 * "last seen a minute ago" instead of a permanent green light. A window alone
 * (without this) would lie: last_seen only moves when a request fires, so
 * someone who opened the app and walked away would look online for as long as
 * the window allowed. The heartbeat is the fix.
 */
function startHeartbeat(): void {
  let timer: number | undefined;
  const beat = () => {
    if (document.visibilityState === 'visible') void heartbeat();
  };
  const start = () => {
    if (timer !== undefined) return;
    beat(); // immediately, so opening the app lights you up now
    timer = window.setInterval(beat, 30_000);
  };
  const stop = () => {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') start();
    else stop(); // backgrounded → stop pinging → go offline within ~75s
  });
  if (document.visibilityState === 'visible') start();
}

/**
 * The service worker caches the shell and the peaks JSON, and nothing else.
 * Never audio — a few dozen takes would fill a cheap phone and then the browser
 * would evict the whole origin, shell included.
 *
 * It is registered last, and its failure is swallowed: a PWA that won't install
 * is a mild disappointment, but a blank screen because a service worker threw
 * during boot is a dead app. And the plain-Safari-tab path must always keep
 * working — iOS 26 still has background-audio bugs in installed PWAs, so the
 * browser tab is the fallback we never break.
 */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}
